import CollectionCard from '@/components/collection-import/CollectionCard'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { AdminHeaderActionButton, AdminPageHeader } from '@/components/patterns/admin-page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { usePathHelper } from '@/hooks/usePathHelper'
import {
  collectionQueries,
  invalidateCollectionImportDependencies,
  useActiveGlobalCollectionImportAttemptQuery,
  useCreateFromGlobalCollectionMutation,
  useGlobalCollectionImportAttemptQuery,
  useGlobalCollections,
} from '@/lib/services/collections/collections.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound, useNavigate, useParams } from '@tanstack/react-router'
import { Check, ChevronDown, ChevronUp, Import, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

function progressUnitLabel(stage) {
  switch (stage) {
    case 'importing_collections':
      return 'collections'
    case 'syncing_products':
      return 'product syncs'
    case 'copying_photos':
      return 'photos'
    case 'updating_smart_collections':
      return 'updates'
    case 'updating_storefront':
      return 'updates'
    case 'finalizing':
    case 'completed':
      return 'steps'
    default:
      return 'items'
  }
}

export const Route = createFileRoute('/_authenticated/$orgId/products/collections/import')({
  component: CollectionImportPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('create', 'collections')) throw notFound()

    await context.queryClient.ensureQueryData(
      collectionQueries.global({ organizationId: params.orgId })
    )
  },
})

function CollectionImportProgressCard({ attempt }) {
  if (!attempt) {
    return null
  }

  const isFailed = attempt.status === 'failed'
  const isCompleted = attempt.status === 'completed'
  const stageProcessed = attempt.progress.stage_processed ?? attempt.progress.processed
  const stageTotal = attempt.progress.stage_total ?? attempt.progress.total
  const progressLabel =
    stageTotal > 0
      ? `${stageProcessed} of ${stageTotal} ${progressUnitLabel(attempt.stage)}`
      : 'Preparing import'
  const title = isCompleted ? 'Import Complete' : isFailed ? 'Import Failed' : 'Import Running'

  return (
    <Card surface={isFailed ? 'destructive' : 'default'}>
      <CardHeader className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{title}</CardTitle>
            <CardDescription>{attempt.message ?? attempt.stage_label}</CardDescription>
          </div>
          <Badge variant={isCompleted ? 'success' : isFailed ? 'destructive' : 'info'}>
            {attempt.stage_label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="h-3 overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={attempt.progress.percent}
          aria-label="Collection import progress"
        >
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-300',
              isFailed ? 'bg-destructive' : 'bg-primary'
            )}
            style={{ width: `${attempt.progress.percent}%` }}
          />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>{progressLabel}</span>
          <span>{attempt.progress.percent}%</span>
        </div>
        {attempt.error?.message ? (
          <p className="text-sm text-destructive">{attempt.error.message}</p>
        ) : null}
      </CardContent>
    </Card>
  )
}

export function CollectionImportPage() {
  const { data: globalCollections } = useGlobalCollections()

  const navigate = useNavigate()
  const { orgId } = useParams({ strict: false })
  const path = usePathHelper()
  const [isOpen, setIsOpen] = useState(() => Array(globalCollections?.length).fill(false))
  const [isProductSelected, setIsProductSelected] = useState(() =>
    globalCollections.map((globalCollection) =>
      Array(globalCollection.products?.length).fill(false)
    )
  )
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [activeImportAttemptId, setActiveImportAttemptId] = useState(null)
  const [createdImportAttempt, setCreatedImportAttempt] = useState(null)
  const handledImportAttemptIdsRef = useRef(new Set())
  const { mutateAsync: importCollections } = useCreateFromGlobalCollectionMutation()
  const activeCollectionImportAttemptQuery = useActiveGlobalCollectionImportAttemptQuery()
  const refetchActiveCollectionImportAttempt = activeCollectionImportAttemptQuery.refetch
  const importAttemptQuery = useGlobalCollectionImportAttemptQuery(activeImportAttemptId, {
    enabled: Boolean(activeImportAttemptId),
  })
  const queriedImportAttempt =
    importAttemptQuery.data?.type === 'global_collection_selection' ? importAttemptQuery.data : null
  const recoveredImportAttempt =
    activeCollectionImportAttemptQuery.data?.type === 'global_collection_selection'
      ? activeCollectionImportAttemptQuery.data
      : null
  const recoveredImportAttemptId = recoveredImportAttempt?.id ?? null
  const createdSelectionImportAttempt =
    createdImportAttempt?.type === 'global_collection_selection' ? createdImportAttempt : null
  const activeImportAttempt =
    queriedImportAttempt ?? recoveredImportAttempt ?? createdSelectionImportAttempt ?? null
  const isImportRunning =
    isPending ||
    activeImportAttempt?.status === 'queued' ||
    activeImportAttempt?.status === 'running'

  useEffect(() => {
    if (!recoveredImportAttemptId || activeImportAttemptId === recoveredImportAttemptId) {
      return
    }

    // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
    setActiveImportAttemptId(recoveredImportAttemptId)
  }, [activeImportAttemptId, recoveredImportAttemptId])

  useEffect(() => {
    if (!activeImportAttempt || handledImportAttemptIdsRef.current.has(activeImportAttempt.id)) {
      return
    }

    if (activeImportAttempt.status === 'completed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)
      void invalidateCollectionImportDependencies(orgId)
      void refetchActiveCollectionImportAttempt?.()
      toast.success('Collections imported successfully')
      // oxlint-disable-next-line react/set-state-in-effect -- grandfathered
      setIsOpen((prev) => prev.map(() => false))
      void navigate({ ...path.collections.index().link })

      return
    }

    if (activeImportAttempt.status === 'failed') {
      handledImportAttemptIdsRef.current.add(activeImportAttempt.id)
      void refetchActiveCollectionImportAttempt?.()
      toast.error('Failed to import collections', {
        description:
          activeImportAttempt.error?.message ??
          activeImportAttempt.message ??
          'The collection import failed.',
      })
    }
  }, [activeImportAttempt, navigate, orgId, path, refetchActiveCollectionImportAttempt])

  const flaggedGlobalCollections = globalCollections.map((globalCollection) => {
    const importStatus = globalCollection.import_status ?? {}

    return {
      ...globalCollection,
      existingCollection: importStatus.is_existing_collection
        ? { id: importStatus.existing_collection_id }
        : null,
      hasNothingToImport: Boolean(importStatus.has_nothing_to_import),
      products: globalCollection.products.map((globalProduct) => ({
        ...globalProduct,
        isExistingProduct: Boolean(globalProduct.import_status?.is_existing_product),
        isInCollection: Boolean(globalProduct.import_status?.is_in_collection),
        disabled: globalProduct.import_status?.is_importable !== true,
      })),
    }
  })

  const handleSetOpen = (index) => (isOpen) => {
    setIsOpen((prev) => {
      const copy = [...prev]
      copy[index] = isOpen
      return copy
    })
  }

  const handleSetProductSelected = (collectionIdx) => (productIdx) => (selected) => {
    if (flaggedGlobalCollections[collectionIdx].hasNothingToImport) return

    const product = flaggedGlobalCollections[collectionIdx].products[productIdx]

    if (product.isExistingProduct && product.isInCollection) return

    setIsProductSelected((prev) => {
      const copy = [...prev]
      copy[collectionIdx][productIdx] = selected
      return copy
    })
  }

  const handleSetCollectionSelected = (collectionIdx) => (selected) => {
    if (flaggedGlobalCollections[collectionIdx].hasNothingToImport) return

    isProductSelected[collectionIdx].forEach((_, productIdx) =>
      handleSetProductSelected(collectionIdx)(productIdx)(selected)
    )
  }

  const handleExpandAll = () => setIsOpen((prev) => prev.map(() => true))

  const handleCollapseAll = () => setIsOpen((prev) => prev.map(() => false))

  const handleSelectAll = () =>
    globalCollections.forEach((_, index) => handleSetCollectionSelected(index)(true))

  const handleDeselectAll = () =>
    globalCollections.forEach((_, index) => handleSetCollectionSelected(index)(false))

  const handleSubmit = async (e) => {
    try {
      e.preventDefault()
      setIsPending(true)
      setCreatedImportAttempt(null)

      const selectedCollections = flaggedGlobalCollections
        .map((globalCollection, globalCollectionIdx) => ({
          ...globalCollection,
          products: globalCollection.products.filter(
            (_, productIdx) => isProductSelected[globalCollectionIdx][productIdx]
          ),
        }))
        .filter((collection) => collection.products.length > 0)

      const attempt = await importCollections({ collections: selectedCollections })

      setActiveImportAttemptId(attempt.id)
      setCreatedImportAttempt(attempt)
      toast.success('Collection import started')
    } catch (error) {
      console.error(error)
      setCreatedImportAttempt(null)
      toast.error('Failed to import collections', { description: getErrorMessage(error) })
    } finally {
      setIsDialogOpen(false)
      setIsPending(false)
    }
  }

  return (
    <div className="space-y-4 p-2">
      <AdminPageHeader
        icon={Import}
        title="Import Collections"
        description="Kickstart your product catalog with our curated list of product collections."
        actions={
          <ConfirmationDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            isPending={isImportRunning}
            variant="question"
            title="Import Collections"
            description="Are you sure you want to import these collections and their products into your store? This action cannot be easily undone."
            confirmText="Import"
            pendingText="Starting..."
            trigger={
              <AdminHeaderActionButton
                disabled={
                  isImportRunning || isProductSelected.flat().every((isSelected) => !isSelected)
                }
              >
                <Import />
                {isImportRunning ? 'Import Running' : 'Import'}
              </AdminHeaderActionButton>
            }
            onConfirm={handleSubmit}
          />
        }
      />
      <CollectionImportProgressCard attempt={activeImportAttempt} />
      <div className="space-y-2 mt-4">
        <Button variant="ghost" onClick={handleExpandAll}>
          <ChevronDown />
          Expand All
        </Button>
        <Button variant="ghost" onClick={handleCollapseAll}>
          <ChevronUp />
          Collapse All
        </Button>
        <Button variant="ghost" onClick={handleSelectAll}>
          <Check />
          Select All
        </Button>
        <Button variant="ghost" onClick={handleDeselectAll}>
          <X />
          Deselect All
        </Button>
        {flaggedGlobalCollections.map((globalCollection, index) => (
          <CollectionCard
            key={globalCollection.id}
            collection={globalCollection}
            isOpen={isOpen[index]}
            setIsOpen={handleSetOpen(index)}
            setIsCollectionSelected={handleSetCollectionSelected(index)}
            isProductSelected={isProductSelected[index]}
            setIsProductSelected={handleSetProductSelected(index)}
            collectionImageLoading="eager"
          />
        ))}
      </div>
    </div>
  )
}
